// Axis 3 — per-project content/output language options.
//
// This is the language of GENERATED content (scripts / dialogue / narration) —
// i.e. what the TTS voice model (`eleven_multilingual_v2`) can speak — NOT the
// (much smaller) set of translated UI locales in `supportedLocales.ts`. It is
// deliberately its OWN constant, mirroring the backend single source of truth
// `dreamwright_core_schemas.content_language.CONTENT_LANGUAGE_CODES`
// (which also backs state_models.VALID_CONTENT_LANGUAGES). Keep the code set in
// sync with it — contentLanguages.parity.test.ts enforces this against the
// Python source.
//
// Independent of the UI language (axis 1) and the Director conversation language
// (axis 2). The "Auto (same as conversation)" entry maps to backend `null` (no
// content directive; the Director infers the content language).
//
// Labels here are endonyms (native language names). The content-language
// dropdown in ProjectConfigGear composes a hybrid "<UI-language name>
// (<endonym>)" label from these plus the shared `languageNames` table; the
// endonym stored here is the parenthetical half.

import type { TFunction } from 'i18next'
import type { VideoResolution } from '../api/types'

export const CONTENT_LANGUAGE_AUTO = 'auto'

export interface ContentLanguageOption {
  // Concrete ISO 639-1 code (or `fil` for Filipino), or the Auto sentinel.
  value: string
  // Endonym for concrete languages; the Auto entry carries an i18n key instead.
  label?: string
  // i18n key for the Auto entry (translated per UI locale).
  labelKey?: string
}

// Mirrors backend CONTENT_LANGUAGE_CODES order (eleven_multilingual_v2 set).
const CONTENT_LANGUAGE_ENDONYMS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'en', label: 'English' },
  { value: 'ja', label: '日本語' },
  { value: 'zh', label: '中文' },
  { value: 'de', label: 'Deutsch' },
  { value: 'hi', label: 'हिन्दी' },
  { value: 'fr', label: 'Français' },
  { value: 'ko', label: '한국어' },
  { value: 'pt', label: 'Português' },
  { value: 'it', label: 'Italiano' },
  { value: 'es', label: 'Español' },
  { value: 'id', label: 'Bahasa Indonesia' },
  { value: 'nl', label: 'Nederlands' },
  { value: 'tr', label: 'Türkçe' },
  { value: 'fil', label: 'Filipino' },
  { value: 'pl', label: 'Polski' },
  { value: 'sv', label: 'Svenska' },
  { value: 'bg', label: 'Български' },
  { value: 'ro', label: 'Română' },
  { value: 'ar', label: 'العربية' },
  { value: 'cs', label: 'Čeština' },
  { value: 'el', label: 'Ελληνικά' },
  { value: 'fi', label: 'Suomi' },
  { value: 'hr', label: 'Hrvatski' },
  { value: 'ms', label: 'Bahasa Melayu' },
  { value: 'sk', label: 'Slovenčina' },
  { value: 'da', label: 'Dansk' },
  { value: 'ta', label: 'தமிழ்' },
  { value: 'uk', label: 'Українська' },
  { value: 'ru', label: 'Русский' },
  { value: 'vi', label: 'Tiếng Việt' },
]

// The canonical set of selectable content-language codes (concrete, no Auto).
export const CONTENT_LANGUAGE_CODES: ReadonlyArray<string> = CONTENT_LANGUAGE_ENDONYMS.map(
  (option) => option.value,
)

// Dropdown options: Auto (→ backend null) first, then the TTS languages.
export const CONTENT_LANGUAGE_OPTIONS: ReadonlyArray<ContentLanguageOption> = [
  { value: CONTENT_LANGUAGE_AUTO, labelKey: 'contentLanguage.auto' },
  ...CONTENT_LANGUAGE_ENDONYMS,
]

// Endonym lookup for rendering a persisted value (falls back to the raw code).
export function contentLanguageEndonym(code: string): string {
  const match = CONTENT_LANGUAGE_ENDONYMS.find((option) => option.value === code)
  return match ? match.label : code
}

// A resolved `<select>` option: the persisted value + a display label.
export interface ContentLanguageSelectOption {
  value: string
  label: string
}

/**
 * Build the content/output-language `<select>` options for a given UI locale.
 *
 * Single source of truth for the hybrid label composition, shared by both the
 * canvas `ProjectConfigGear` and the landing `ProjectPlanModal` so the two never
 * drift. The Auto entry uses the shared `contentLanguageAuto` key; concrete
 * languages use the "<UI-language name> (<endonym>)" convention (the shared
 * `languageOptionLabel` + `languageNames` table). When the UI-language name and
 * the endonym coincide (e.g. English UI + English) the label collapses to one
 * term so it never reads "English (English)".
 *
 * All keys are `shared:`-prefixed, so this resolves from ANY host `t` regardless
 * of its default namespace (the `shared` bundle is globally loaded).
 */
export function buildContentLanguageOptions(t: TFunction): ContentLanguageSelectOption[] {
  return CONTENT_LANGUAGE_OPTIONS.map((option) => {
    if (option.value === CONTENT_LANGUAGE_AUTO) {
      return { value: option.value, label: t('shared:contentLanguageAuto') }
    }
    const endonym = option.label ?? option.value
    const name = t('shared:languageNames.' + option.value)
    const label =
      name === endonym ? endonym : t('shared:languageOptionLabel', { name, endonym })
    return { value: option.value, label }
  })
}

// Persistent "Output Quality" preference: project-level default output
// resolution stamped onto newly-created video nodes' in_resolution. The value
// set is the Veo ladder (the default video model). Native models honor it
// (Gemini Omni is fixed at 720p); BYOM uses its own declared resolutions.
// Labels are universal (no i18n). Shared by `ProjectConfigGear` (canvas) and
// `ProjectPlanModal` (landing) — one source so the option set never drifts.
export const VIDEO_RESOLUTION_OPTIONS: ReadonlyArray<{
  value: VideoResolution
  label: string
}> = [
  { value: '720p', label: '720p' },
  { value: '1080p', label: '1080p' },
  { value: '4k', label: '4K' },
]

// The create-time default output resolution — mirrors the backend
// `state_models.DEFAULT_VIDEO_RESOLUTION` ("1080p"). The landing plan modal
// pre-selects this; the create payload omits `video_resolution` when it is left
// at this default (the backend applies the same default anyway).
//
// These two constants MUST stay equal: the payload is omitted when the user
// leaves the default alone, so a mismatch means the console shows one
// resolution and the backend stamps another, with nothing to catch it.
export const DEFAULT_VIDEO_RESOLUTION: VideoResolution = '1080p'
